import { useEffect, useRef, useState } from 'react';
import { Flex, Spin, Typography } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import logo from '../../assets/check-vin-logo.png';
import classes from '../../common/styles/FormStyles.module.scss';
import { LanguageSelect } from '../../common/components/languageSelect/languageSelect';
import { constants } from '../../common/constants/constants';
import { spacing } from '../../theming';
import { getUnsubscribeToken } from '../../services/unsubscribeLink';
import { isImpersonationTab } from '../../services/impersonationSession';
import { subscribeByLink, unsubscribeByLink, UNSUBSCRIBE_STATES } from '../../services/emailPreferencesService';
import StateView from './StateView';

const S = UNSUBSCRIBE_STATES;
const ACTIONS = { UNSUBSCRIBE: 'unsubscribe', SUBSCRIBE: 'subscribe' };

// what each state shows: its icon, its copy (unsubscribe:<key>.title / .text) and which actions it offers
const VIEWS = {
    [S.CONFIRM]: { icon: 'mail', key: 'confirm', primary: 'unsubscribe' },
    [S.DONE]: { icon: 'ok', key: 'done', subscribeAgain: true, manage: true },
    [S.ALREADY]: { icon: 'info', key: 'already', subscribeAgain: true, manage: true },
    [S.SUBSCRIBED]: { icon: 'ok', key: 'subscribed', manage: true },
    [S.INVALID]: { icon: 'warn', key: 'invalid', primary: 'manage' },
    [S.USE_SETTINGS]: { icon: 'info', key: 'useSettings', primary: 'manage' },
    [S.RETRY]: { icon: 'warn', key: 'retry', primary: 'retry' },
    [S.NO_TOKEN]: { icon: 'warn', key: 'noToken', manage: true },
    [S.IMPERSONATING]: { icon: 'warn', key: 'impersonating' },
};

// nothing is sent on open: the page waits for a click, so a link scanner that runs the page changes nothing
const initialState = token => {
    if (isImpersonationTab()) {
        return S.IMPERSONATING;
    }
    return token ? S.CONFIRM : S.NO_TOKEN;
};

// no-referrer for everything this page loads, while it is shown
const useNoReferrer = () => {
    useEffect(() => {
        const meta = document.createElement('meta');
        meta.name = 'referrer';
        meta.content = 'no-referrer';
        document.head.appendChild(meta);

        return () => meta.remove();
    }, []);
};

/**
 * Public page behind the "Unsubscribe" link of a marketing email (/unsubscribe?lang=..#t=<token>). The pre-init
 * (unsubscribePreInit.js) has already moved the token into memory and out of the URL. Every change waits for a
 * click, and each click sends exactly one request.
 */
export default function Unsubscribe() {
    const { t, i18n } = useTranslation(['unsubscribe']);
    const [token] = useState(getUnsubscribeToken);
    const [state, setState] = useState(() => initialState(token));
    const busyRef = useRef(false);
    const lastActionRef = useRef(ACTIONS.UNSUBSCRIBE);
    const headingRef = useRef(null);
    const isFirstState = useRef(true);

    useNoReferrer();

    useEffect(() => {
        document.title = t('unsubscribe:pageTitle');
    }, [t]);

    // each new state is announced by moving focus to its heading (keyboard and screen-reader users)
    useEffect(() => {
        if (isFirstState.current) {
            isFirstState.current = false;
            return;
        }
        headingRef.current?.focus();
    }, [state]);

    const run = async action => {
        if (busyRef.current || !token || isImpersonationTab()) {
            return;
        }
        busyRef.current = true;
        lastActionRef.current = action;
        setState(S.LOADING);

        const send = action === ACTIONS.SUBSCRIBE ? subscribeByLink : unsubscribeByLink;
        const next = await send(token);

        busyRef.current = false;
        setState(next);
    };

    return (
        <div className={classes['auth-wrapper']}>
            <main className={classes['auth-card']} style={{ textAlign: 'center' }}>
                <a href={constants.LANDING_BASE_URL}>
                    <img src={logo} alt="CheckVIN.ai" className={classes.logo} />
                </a>
                <div aria-live="polite" style={{ width: '100%' }}>
                    {state === S.LOADING ? (
                        <Flex vertical align="center" gap={spacing[3]}>
                            <Spin indicator={<LoadingOutlined style={{ fontSize: 40 }} spin />} />
                            <Typography.Text>{t('unsubscribe:loading')}</Typography.Text>
                        </Flex>
                    ) : (
                        <StateView
                            view={VIEWS[state]}
                            t={t}
                            headingRef={headingRef}
                            onUnsubscribe={() => run(ACTIONS.UNSUBSCRIBE)}
                            onSubscribe={() => run(ACTIONS.SUBSCRIBE)}
                            onRetry={() => run(lastActionRef.current)}
                        />
                    )}
                </div>
            </main>
            <footer>
                <LanguageSelect i18n={i18n} isUseRequest={false} ariaLabel={t('unsubscribe:language')} />
            </footer>
        </div>
    );
}
